/**
 * Utilities for redux reducer. I implemented these because there are way too many
 * alternatives to sift through for such simple utilities.
 * See: https://github.com/markerikson/redux-ecosystem-links/blob/master/reducers.md
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import produce from 'immer'

/**
 * Replacement for redux combineReducers because it is too opinionated
 * @param {Object} reducersMap The key-reducer pairs
 */
export const combineReducers = (reducersMap) => {
  const keys = Object.keys(reducersMap)

  return produce((draft, action) => {
    for (const key of keys) {
      draft[key] = reducersMap[key](draft[key], action)
    }
  }, {})
}

/**
 * Allows partially combining reducers for SOME keys unlike combineReducers that
 * forces you to define reducers for ALL keys
 * @param {Function} rootReducer The root reducer
 * @param {Object} reducersMap The key-reducer pairs for nesting
 */
export const nestReducers = (rootReducer, reducersMap) => {
  const attrs = Object.keys(reducersMap)

  return produce((draft, action) => {
    const draftCopy = Object.assign({}, draft)
    Object.assign(draft, rootReducer(draft, action))

    for (const attr of attrs) {
      draft[attr] = reducersMap[attr](draftCopy[attr], action)
    }
  }, rootReducer(undefined, {}))
}

/**
 * Pipes (composeLeft) reducers
 * @param  {...Function} reducers The reducers
 */
export const pipeReducers = (...reducers) => {
  const initStatus = reducers.reduce((acc, reducer) => {
    return Object.assign(acc, reducer(undefined, {}))
  }, {})

  return produce((draft, action) => {
    for (const reducer of reducers) {
      const result = reducer(draft, action)
      if (typeof result !== 'undefined') {
        Object.assign(draft, result)
      }
    }
  }, initStatus)
}
